import { donatePlex } from 'services/user/plex'
import UserContextModel from 'models/UserContextModel'
import { getPlainTextFromRichText } from '../../../utils'
import { PlexForGoodTranslations } from '../../../translations'

interface DonationParams {
    plexAmount: number
    campaignId: string
    characterId: number
}

export interface ValidationErrors {
    plexAmount?: string
    campaignId?: string
    characterId?: string
}

// Maps API error codes to translation keys
export const ERROR_CODE_TO_TRANSLATION_KEY: Record<
    string,
    keyof PlexForGoodTranslations
> = {
    // Donation related
    DonationValidationError: 'errorUnexpected',
    DonationInsufficientBalance: 'errorInsufficientPlex',
    DonationDuplicateAttempt: 'errorUnexpected',
    DonationProcessingError: 'errorServerIssue',
    DonationInvalidCampaign: 'errorInvalidCampaign',
    DonationInvalidCharacter: 'errorSelectCharacter',
    DonationInvalidAmount: 'errorInvalidAmount',
    // Character related - all character errors map to same message
    CharacterNotFound: 'errorSelectCharacter',
    CharacterInvalidOwnership: 'errorSelectCharacter',
    CharacterRetrievalError: 'errorServerIssue',
    // System related - consolidated to a single server error message
    SystemUnexpectedError: 'errorServerIssue', // Changed from errorUnexpected to errorServerIssue
    SystemServiceUnavailable: 'errorServerIssue',
    // Legacy error codes (kept for backward compatibility)
    InvalidCampaign: 'errorInvalidCampaign',
    InsufficientPlex: 'errorInsufficientPlex',
    InvalidCharacter: 'errorSelectCharacter',
    ServerError: 'errorServerIssue',
    Unauthorized: 'errorSessionExpired',
    FetchPlexBalanceError: 'failedToFetchPlexBalance',
    _default: 'errorServerIssue', // Changed from errorUnexpected to errorServerIssue
}

// Maps API error codes to field keys for validation errors
const ERROR_CODE_TO_FIELD_MAP = {
    // Donation related
    DonationInsufficientBalance: 'plexAmount',
    DonationInvalidCharacter: 'characterId',
    DonationInvalidAmount: 'plexAmount',
    // Character related
    CharacterNotFound: 'characterId',
    CharacterInvalidOwnership: 'characterId',
    // Legacy error codes (kept for backward compatibility)
    InsufficientPlex: 'plexAmount',
    InvalidCharacter: 'characterId',
}

/**
 * Validates a donation request before submission
 * @param donationAmount The amount of PLEX to donate
 * @param plexBalance The user's current PLEX balance
 * @param selectedCampaign The selected campaign or null
 * @param selectedCharacterId The selected character ID or undefined
 * @param charactersCount The number of characters the user has
 * @param translations Translations for validation error messages
 * @returns An object with validation errors or an empty object if valid
 */
export const validateDonation = (
    donationAmount: number,
    plexBalance: UserContextModel['plexBalance'],
    selectedCampaign: { id: string } | null,
    selectedCharacterId?: number,
    charactersCount = 0,
    translations?: PlexForGoodTranslations
): ValidationErrors => {
    const errors: ValidationErrors = {}

    if (!selectedCampaign) {
        errors.campaignId = translations
            ? getPlainTextFromRichText(translations.errorSelectCampaign)
            : 'Please select a campaign'
    }

    if (!donationAmount || donationAmount <= 0) {
        errors.plexAmount = translations
            ? getPlainTextFromRichText(translations.errorInvalidAmount)
            : 'Please enter a valid donation amount'
    } else if (donationAmount > Number(plexBalance)) {
        errors.plexAmount = translations
            ? getPlainTextFromRichText(translations.errorInsufficientPlex)
            : 'Insufficient PLEX balance'
    }

    // Character validation with specific message for no characters
    if (charactersCount === 0) {
        errors.characterId = translations
            ? getPlainTextFromRichText(translations.mustHaveCharacter)
            : 'You need at least one character to donate PLEX'
    } else if (!selectedCharacterId) {
        errors.characterId = translations
            ? getPlainTextFromRichText(translations.errorSelectCharacter)
            : 'Please select a character'
    }

    return errors
}

/**
 * Process a donation to a campaign
 * @param userToken JWT token for authentication
 * @param params Donation parameters including amount, campaign and character
 * @param onStart Callback to invoke when donation starts
 * @param onComplete Callback to invoke when donation completes
 * @param translations Translations for error messages
 */
export const processDonation = async (
    userToken: string,
    params: DonationParams,
    onStart: () => void,
    onComplete: (
        success: boolean,
        errors?: ValidationErrors,
        message?: string,
        errorCode?: string
    ) => void,
    translations?: PlexForGoodTranslations
): Promise<void> => {
    // Start loading state
    onStart()

    try {
        const response = await donatePlex(userToken, params)

        if (response.success) {
            // Handle success - pass no errors or messages
            onComplete(true)
        } else {
            // Handle API error
            const errorCode = response.errorCode || '_default'
            const translationKey =
                ERROR_CODE_TO_TRANSLATION_KEY[errorCode] ||
                ERROR_CODE_TO_TRANSLATION_KEY._default

            // Get user-friendly error message
            let userFriendlyMessage = 'An unexpected error occurred'
            if (translations && translationKey) {
                const translationValue = translations[translationKey]
                if (translationValue) {
                    userFriendlyMessage = getPlainTextFromRichText(
                        translationValue
                    )
                }
            }

            // Check if this error should be mapped to a specific field
            const fieldKey = ERROR_CODE_TO_FIELD_MAP[errorCode]
            const errors: ValidationErrors = {}

            // If we have a field mapping for this error, use it as a field error
            // Otherwise, treat it as a global error
            if (fieldKey) {
                errors[fieldKey] = userFriendlyMessage
                // For field errors, don't pass an error code to avoid showing in global error
                onComplete(false, errors, '', null)
            } else {
                // Pass the message as a global error
                onComplete(false, {}, userFriendlyMessage, errorCode)
            }
        }
    } catch (error) {
        // Handle unexpected errors as global errors
        let errorMessage =
            'An unexpected error occurred. Please try again later.'
        if (translations && translations.errorUnexpected) {
            errorMessage = getPlainTextFromRichText(
                translations.errorUnexpected
            )
        }
        onComplete(false, {}, errorMessage, 'UnexpectedError')
    }
}
